/**
 * Where a citizen can move TO, today and next. One list, for every surface
 * that offers a destination.
 *
 * It lived as three `Set`s inside the Journey Builder's country widget — a
 * product decision (which corridors are configured) held in a component. The
 * citizen setup (Citizen first run) shows the destination too, and a second
 * copy is how "Portugal only" stays true in one place and quietly stops being
 * true in the other the day Spain opens.
 *
 * Opening a corridor is a content change first (TelePortal kits, CheckUp
 * persona, QultureUp worlds — see `BL-183`'s Spain gap): move its code from
 * `NEXT_DESTINATIONS` to `OPEN_DESTINATIONS` only when that content ships.
 */

/** Fully configured: a citizen can build a journey here today. */
export const OPEN_DESTINATIONS: readonly string[] = ['PT'];

/** The next corridors — shown by name, disabled, as "coming soon". */
export const NEXT_DESTINATIONS: readonly string[] = ['ES', 'IT'];

/** The roadmap beyond them — shown with a "soon" badge, disabled. */
export const ROADMAP_DESTINATIONS: readonly string[] = [
  // EU member states (other than the open and next corridors)
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'RO',
  'SK',
  'SI',
  'SE',
  // Non-EU key markets
  'GB',
  'US',
  'CN',
  'AE',
];

/** The destination a self-built journey takes when only one is open. */
export function soleOpenDestination(): string | null {
  return OPEN_DESTINATIONS.length === 1 ? OPEN_DESTINATIONS[0] : null;
}

/**
 * Whether a journey or an invitation may go to this country TODAY — only an
 * OPEN destination. Everything else (the next corridors, the roadmap, any
 * other code) has no relocation kit, CheckUp persona or QultureUp world, so a
 * journey there is one the platform cannot serve; an invitation to one is
 * refused at every door (API schemas, the journey factory, the invitation
 * writes), not only hidden in a picker. Case-insensitive.
 */
export function isOpenDestination(code: string | null | undefined): boolean {
  return !!code && OPEN_DESTINATIONS.includes(code.trim().toUpperCase());
}
